import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { taskApi } from '../../api/task.api';
import { useAuth } from '../../store/AuthContext';
import { useUI } from '../../store/UIContext';
import { Badge, Btn, EmptyState, PageHeader, Select, Spinner } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { TASK_FILTERS, deadlineCountdown, formatTaskDate, taskStatusInfo } from '../../utils/taskHelpers';

const SORTS = [
  { value: 'deadline:asc', label: 'ددلاین (نزدیک‌ترین)' },
  { value: 'deadline:desc', label: 'ددلاین (دورترین)' },
  { value: 'created:desc', label: 'تاریخ تعریف (جدیدترین)' },
  { value: 'created:asc', label: 'تاریخ تعریف (قدیمی‌ترین)' },
];

/** Employees see tasks assigned to them; a manager can switch to the tasks
 * they defined (with each task's current status, drafts included). */
export default function TaskListPage() {
  const { user } = useAuth();
  const { addToast } = useUI();
  const isManager = user?.type === 'manager_employee';
  const [params, setParams] = useSearchParams();
  const scope = isManager && params.get('scope') === 'created' ? 'created' : 'mine';
  const status = params.get('status') || '';
  const [sort, setSort] = useState('deadline:asc');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async (spinner = true) => {
    if (spinner) setLoading(true);
    const [sort_by, order] = sort.split(':');
    try {
      const res = await taskApi.list({ scope, status, sort: sort_by, order });
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch {
      if (spinner) addToast('خطا در بارگذاری تسک‌ها', 'error');
    }
    setLoading(false);
  }, [scope, status, sort, addToast]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(false));

  const tabStyle = (active) => ({
    padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
    fontSize: 13, fontWeight: 600, background: active ? '#1B3068' : '#f1f5f9', color: active ? '#fff' : '#475569',
  });

  return (
    <div className="fade-in">
      <PageHeader
        title="تسک‌ها"
        subtitle={scope === 'created' ? 'تسک‌هایی که برای کارمندان تعریف کرده‌اید' : 'تسک‌هایی که به شما محول شده است'}
        action={isManager && <Link to="/tasks/new"><Btn>+ تسک جدید</Btn></Link>}
      />

      {isManager && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button style={tabStyle(scope === 'mine')} onClick={() => setParam('scope', '')}>تسک‌های من</button>
          <button style={tabStyle(scope === 'created')} onClick={() => setParam('scope', 'created')}>تسک‌های تعریف‌شده</button>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        {TASK_FILTERS.filter((f) => scope === 'created' || f.id !== 'responded').map((f) => (
          <button key={f.id} style={tabStyle(status === f.id)} onClick={() => setParam('status', f.id)}>{f.label}</button>
        ))}
        <Select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          options={SORTS}
          wrapStyle={{ marginRight: 'auto', minWidth: 210 }}
        />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>
      ) : !tasks.length ? (
        <EmptyState title="تسکی یافت نشد" subtitle="با این فیلتر تسکی وجود ندارد." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tasks.map((t) => {
            const info = t.status === 'draft' ? { label: 'پیش‌نویس', variant: 'gray' } : taskStatusInfo(t);
            const target = t.status === 'draft' ? `/tasks/new?draft=${t.task_id}` : `/tasks/${t.task_id}`;
            const active = t.status === 'open' || t.status === 'responded';
            return (
              <Link key={t.task_id} to={target} style={{ textDecoration: 'none' }}>
                <div style={{
                  background: '#fff', border: `1px solid ${t.overdue && active ? '#fecaca' : '#e2e8f0'}`,
                  borderRadius: 12, padding: '14px 16px', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
                }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
                      {!t.assignee_seen && scope === 'mine' && <span style={{ color: '#ef4444' }}>● </span>}
                      {t.title || '(بدون عنوان)'}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                      {scope === 'created' ? `برای: ${t.assignee_name || '—'}` : `از: ${t.creator_name}`}
                      {t.sent_at && ` · تعریف: ${formatTaskDate(t.sent_at)}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'left', fontSize: 12, color: t.overdue && active ? '#b91c1c' : '#475569' }}>
                    {t.deadline && <div>ددلاین: {formatTaskDate(t.deadline)}</div>}
                    {t.deadline && active && <div style={{ fontWeight: 600 }}>{deadlineCountdown(t.deadline)}</div>}
                  </div>
                  <Badge variant={info.variant}>{info.label}</Badge>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
